import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Icon } from '@workboard/action-item';
import { getAiTypeAttributes, getStateComponentIcon, getStateObjectLabel } from '../../board/utils';
import { WbEventHandlerMixins } from '../../utils/common';

export interface AgentActionItem {
  id: number;
  description: string;
  state: number;
  rag: string | null;
  due_date: number;
  last_mod: number;
}

// tbl_activity.state: 0 = done; everything else is still in flight.
const DONE = 0;

type Tab = 'open' | 'done';

export const AgentActionItems = ({ agentId }: { agentId: number }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<AgentActionItem[] | null>(null);
  const [tab, setTab] = useState<Tab>('open');
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setFailed(false);
    axios
      .get(`${wbConfig.baseUrl}/people/agentActionItems`, { params: { id: agentId } })
      .then((res) => live && setItems(res.data?.items ?? []))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [agentId, attempt]);

  const { attributes } = getAiTypeAttributes(0);
  const states = attributes?.state?.values ?? [];
  const shown = (items ?? []).filter((ai) => (tab === 'done') === (ai.state === DONE));

  return (
    <div className="mp-container" data-testid="agent-action-items">
      <h3 className="mp-card-header">{t('ACTION ITEMS')}</h3>
      <div className="agent-ai-tabs" role="tablist">
        {(['open', 'done'] as Tab[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`gbl-btn ${tab === key ? '' : 'gbl-btn-cancel'} m-r-1`}
            onClick={() => setTab(key)}
            data-testid="agent-action-items-tab"
            data-tab={key}
          >
            {key === 'open' ? t('Open') : t('Done')}
          </button>
        ))}
      </div>
      {failed && (
        <p className="no-data-text" data-testid="agent-action-items-error">
          {t("Couldn't load action items.")}
          {' '}
          <button type="button" className="gbl-btn" onClick={() => setAttempt((n) => n + 1)} data-testid="agent-action-items-retry">
            {t('Retry')}
          </button>
        </p>
      )}
      {!failed && items === null && <p className="no-data-text">{t('Loading…')}</p>}
      {!failed && items !== null && shown.length === 0 && (
        <p className="no-data-text" data-testid="agent-action-items-empty">
          {tab === 'open' ? t('No open action items.') : t('No completed action items yet.')}
        </p>
      )}
      <ul className="agent-ai-list" style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
        {shown.map((ai) => {
          const stateLabel = getStateObjectLabel(states, ai.state);
          const when = ai.due_date > 0 ? ai.due_date : ai.last_mod;
          return (
            <li key={ai.id} style={{ borderBottom: '1px solid #ebeef2' }}>
              <button
                type="button"
                data-testid="agent-action-item"
                data-ai-id={ai.id}
                className="wb-pointer"
                style={{
                  display: 'flex', gap: '10px', alignItems: 'center', width: '100%', padding: '8px 0', background: 'none', border: 0, textAlign: 'left',
                }}
                onClick={(e) => WbEventHandlerMixins.aiDetailPage(ai.id, e.currentTarget, 'true')}
              >
                <span role="img" aria-label={stateLabel} title={stateLabel}>
                  <Icon name={getStateComponentIcon(states, ai.state)} width="16" height="16" />
                </span>
                <span style={{
                  flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
                >
                  {ai.description}
                </span>
                {when > 0 && (
                  <span className="no-data-text" style={{ whiteSpace: 'nowrap' }}>
                    {moment.unix(when).format('MMM DD')}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
